import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Menu } from 'app/shared/models/control/menu';
import { AuthorizeService } from 'app/shared/services/control/authorization/authorization.service'; // leaf import - the app/shared/services/control barrel pulls in the whole control service set
import { DynamicFormService } from 'app/shared/services/dynamicform/dynamicform.service';

/**
 * Base for the ids of the Tools rail module and the nodes under it. PagesMenu's own ids stop well
 * below this (its highest is 3336), so a shared module can never collide with a static node - ids
 * are what MenuKey chains, favorites and the recent-usage counters are built from.
 */
export const TOOLS_MODULE_ID = 90000;

/** The Tools rail module itself - its children are filled in per user, see loadToolsModule(). */
const TOOLS_MODULE: Menu = {
    id: TOOLS_MODULE_ID,
    title: 'Tools',
    path: 'tools',
    icon: 'fa-solid fa-toolbox fs-17',
    isMain: true,
    children: [],
};

/** The fields of a Module/GetSharedModules row that the menu needs. */
interface SharedModule {
    Id: number;
    Name: string;
    RouteTitle: string;
}

/**
 * Builds the rail's "Tools" module out of the shared dynamic (smart-form) modules the logged-in
 * user is allowed to open - the "Integration"/"Dental Lab" style modules authored in the
 * smart-forms system rather than in PagesMenu.
 *
 * These used to hang off the bottom of the old sidebar as extra top-level entries of their own
 * (ba-dynamic-modules-menu, inside ba-main-menu). That sidebar is no longer rendered - the shell
 * redesign replaced it with the rail + flyout - so nothing showed them any more. They come back
 * here as the children of one rail module instead of one rail entry each: the rail is a fixed
 * list of hospital systems, and a per-user, DB-authored module appearing there as a peer of
 * Clinical Information System would make the rail itself change shape from user to user.
 *
 * Kept out of PagesMenu on purpose: PagesMenu is a static compile-time tree that several services
 * index once at construction, while this list is per-user and arrives over HTTP.
 */
@Injectable({ providedIn: 'root' })
export class SharedModulesMenuService {

    constructor(
        private readonly dynamicForms: DynamicFormService,
        private readonly authorize: AuthorizeService,
    ) { }

    /**
     * The Tools module, or null when there is nothing to put in it - no shared module exists, the
     * user may open none of them, or the smart-forms API did not answer. Null rather than an empty
     * module so the rail never shows a Tools icon that opens an empty panel.
     *
     * A failed call is reported as null instead of an error: the smart-forms API is a separate
     * service that the rest of the shell does not depend on (and that is not deployed locally at
     * all), so a toast on every page load would be noise. The old sidebar menu did surface it.
     */
    loadToolsModule(): Observable<Menu | null> {
        return this.dynamicForms.getAllModules().pipe(
            map((modules: SharedModule[]) => this.toToolsModule(modules)),
            // Logged rather than swallowed outright: the rail carrying on without Tools is the
            // right behaviour, but a 401/CORS/500 on this endpoint would otherwise leave no
            // trace anywhere of why the module is missing.
            catchError(error => {
                console.warn('[Tools] shared-modules call failed', error?.status, error?.message || error);
                return of(null);
            }),
        );
    }

    private toToolsModule(modules: SharedModule[]): Menu | null {
        const children = (modules || [])
            .filter(m => !!m && !!m.RouteTitle && this.authorize.hasPermission(permissionOf(m)))
            .map(m => toolsChild(m));
        return children.length ? { ...TOOLS_MODULE, children } : null;
    }
}

/**
 * The permission that grants a shared module, named exactly as the Role Permission screen
 * registers it (see role-permission.component.ts's getAllModules) - 'mod_' + the route title with
 * its inner whitespace collapsed to underscores. This is the per-user access added by #54476, so
 * the two spellings have to stay in step.
 */
function permissionOf(module: SharedModule): string {
    return `mod_${module.RouteTitle.trim().replace(/\s+/g, '_')}`;
}

/**
 * One shared module as a menu leaf. `isDynamicModule` makes MenuNavigationService open it in the
 * smart-forms viewer in a new browser tab, which is the only way these screens can be reached -
 * they are not Angular routes of this app, so `url` is deliberately not routable on its own.
 */
function toolsChild(module: SharedModule): Menu {
    return {
        id: TOOLS_MODULE_ID + module.Id,
        title: module.Name,
        path: `dynamicModule/${module.RouteTitle}`,
        url: `/dynamicModule/${module.RouteTitle}`,
        icon: 'fa-solid fa-cube',
        lastChild: true,
        isDynamicModule: true,
    };
}
